<script setup lang="ts">
  import type { SwiperSettings } from '@/components/ui/slider/types'
  import '@/assets/css/swiper.css'
  import { SCREEN_LG } from '@/tailwind.config'
  import type { IImageSlide } from '@/components/clp/influencer-block/types'
  import { SWIPER_NEXT_EL_ID, SWIPER_PREV_EL_ID } from '@/components/clp/constants'

  const props = withDefaults(
    defineProps<{
      slides: IImageSlide[]
      destinationMode?: boolean
    }>(),
    {
      destinationMode: false
    }
  )

  const emit = defineEmits<{ 'navigation-click': []; swipe: [] }>()
  const { isDesktop } = useBTBreakpoints()

  const instanceUid = getCurrentInstance()?.uid ?? 'default'
  const prevButtonId = props.destinationMode ? `${SWIPER_PREV_EL_ID}-${instanceUid}` : SWIPER_PREV_EL_ID
  const nextButtonId = props.destinationMode ? `${SWIPER_NEXT_EL_ID}-${instanceUid}` : SWIPER_NEXT_EL_ID

  const { t } = useI18n()

  const sliderSettings: SwiperSettings = {
    speed: 500,
    keyboard: false,
    slidesPerView: 'auto',
    navigation: false,
    spaceBetween: '16px',
    loop: true,
    breakpoints: {
      [SCREEN_LG]: {
        slidesPerView: 3,
        navigation: {
          nextEl: `#${nextButtonId}`,
          prevEl: `#${prevButtonId}`
        },
        spaceBetween: '24px',
        loop: false
      }
    },
    watchSlidesProgress: true,
    a11y: {
      prevSlideMessage: t('clp.influencerBlock.previousImage'),
      nextSlideMessage: t('clp.influencerBlock.nextImage'),
      slideLabelMessage: ''
    }
  }

  const failedToLoadImages = ref(new Set<string>())

  function onImageFailedToLoad(imageUrl: string) {
    failedToLoadImages.value.add(imageUrl)
  }

  const SWIPE_SUPPRESSION_WINDOW_MS = (sliderSettings.speed ?? 500) + 100

  let suppressNextSwipe = false
  let suppressionTimeoutId: ReturnType<typeof setTimeout> | undefined

  function onSlidesLengthChange() {
    suppressNextSwipe = true
    clearTimeout(suppressionTimeoutId)
    suppressionTimeoutId = setTimeout(() => {
      suppressNextSwipe = false
    }, SWIPE_SUPPRESSION_WINDOW_MS)
  }

  function onSlideChangeTransitionStart() {
    if (suppressNextSwipe) {
      suppressNextSwipe = false
      clearTimeout(suppressionTimeoutId)
      return
    }

    if (!isDesktop.value) emit('swipe')
  }
</script>
<template>
  <UiSliderComponent
    :slider-settings="sliderSettings"
    :slides="slides"
    slide-class="max-w-[80%] sm:max-w-[40%] lg:max-w-full"
    class="influencer-slider relative mb-8 lg:mb-14"
    @swipernavigationprev="emit('navigation-click')"
    @swipernavigationnext="emit('navigation-click')"
    @swiperslidechangetransitionstart="onSlideChangeTransitionStart"
    @swiperslideslengthchange="onSlidesLengthChange"
  >
    <template #slide="{ slide }">
      <NuxtImg
        v-if="!failedToLoadImages.has(slide.image)"
        :src="slide.image"
        class="h-100 w-full rounded-md object-cover md:h-140 xl:h-176"
        loading="lazy"
        alt=""
        @error="onImageFailedToLoad(slide.image)"
      />
      <div
        v-else-if="!destinationMode"
        class="flex h-100 w-full animate-pulse items-center justify-center rounded-md bg-gray-300 text-grey-dark md:h-140 xl:h-176"
      >
        {{ $t('global.error.imageNotFound') }}
      </div>
    </template>
  </UiSliderComponent>
  <div
    v-if="destinationMode"
    class="absolute -left-5 top-[calc(50%-2rem)] hidden w-[calc(100%+2.5rem)] justify-between lg:flex"
  >
    <button
      :id="prevButtonId"
      class="prev-btn z-5 cursor-pointer rounded-full border border-transparent bg-grey p-3 disabled:hidden"
    >
      <Icon
        name="common/chevron-left"
        class="text-bt-blue-dark"
      />
    </button>
    <button
      :id="nextButtonId"
      class="z-5 ml-auto cursor-pointer rounded-full border border-transparent bg-grey p-3 disabled:hidden"
    >
      <Icon
        name="common/chevron-right"
        class="text-bt-blue-dark"
      />
    </button>
  </div>
</template>
